import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import useStyles from '../InlineAutocomplete/InlineAutocomplete.styles';
import type {
  InlineEditAutocompleteDraft,
  InlineEditAutocompleteProps
} from './InlineEditAutocomplete.types';

const InlineEditAutocomplete = ({
  value,
  availableOptions,
  placeholder = 'Add tags...',
  noOptionsText = 'No options',
  onAdd,
  onRemove,
  disabled = false,
  allowCustomInput = true,
  readOnlyValues = []
}: InlineEditAutocompleteProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<InlineEditAutocompleteDraft | null>(null);
  const [addedTags, setAddedTags] = useState<string[]>([]);
  const [removedTags, setRemovedTags] = useState<string[]>([]);
  const { classes } = useStyles({ disabled });
  const currentDraft =
    draft && isEqual(draft.sourceValue, value) ? draft : null;

  if (draft && !currentDraft) {
    setDraft(null);
    setAddedTags([]);
    setRemovedTags([]);
  }

  const selectedOptions = Array.from(
    new Set([...readOnlyValues, ...(currentDraft?.values ?? value)])
  );

  // Combine and sort all values in ascending order, marking which are read-only
  const sortedAllValues = selectedOptions
    .map((val) => ({
      value: val,
      isReadOnly: readOnlyValues.includes(val)
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

  const handleCancel = useCallback(() => {
    setDraft(null);
    setAddedTags([]);
    setRemovedTags([]);
    setIsEditing(false);
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    // Ensure fixed options (readOnlyValues) are always preserved
    const editableValues = newValue.filter(
      (option) => !readOnlyValues.includes(option)
    );
    setDraft({ sourceValue: value, values: editableValues });

    // Track additions and removals (excluding read-only values)
    setAddedTags(editableValues.filter((tag) => !value.includes(tag)));
    setRemovedTags(value.filter((tag) => !editableValues.includes(tag)));
  };

  const handleEdit = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
    }
  }, [disabled]);

  const handleSave = () => {
    if (currentDraft) {
      addedTags
        .filter((tag) => !readOnlyValues.includes(tag))
        .forEach((tag) => {
          onAdd(tag);
        });
      removedTags
        .filter((tag) => !readOnlyValues.includes(tag))
        .forEach((tag) => {
          onRemove(tag);
        });
    }
    setAddedTags([]);
    setRemovedTags([]);
    setIsEditing(false);
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleCancel]
  );

  // Combine all available options including read-only ones, removing duplicates
  const allOptions = [
    ...readOnlyValues,
    ...availableOptions.filter((option) => !readOnlyValues.includes(option))
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (
        isEditing &&
        !containerRef?.current?.contains(event.target as Node) &&
        !target.closest('.MuiAutocomplete-popper')
      ) {
        handleSave();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleSave, isEditing]);

  useEffect(() => {
    if (isEditing && autocompleteRef.current) {
      // Small delay to ensure the component is rendered
      const focusTimeout = setTimeout(() => {
        const input = autocompleteRef.current?.querySelector('input');
        if (input) {
          input.focus(); // Force focus on the input (prevents user from having to double click to add tags)
        }
      }, 0);

      return () => {
        clearTimeout(focusTimeout);
      };
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <Box ref={containerRef} className={classes.inlineAutocomplete}>
        <Autocomplete
          ref={autocompleteRef}
          disableClearable
          open
          multiple
          freeSolo={allowCustomInput}
          onBlur={handleSave}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          options={allOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
          renderValue={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const isFixed = readOnlyValues.includes(option);
              const { key, ...tagProps } = getTagProps({ index });

              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option}
                  onDelete={isFixed ? undefined : tagProps.onDelete}
                  variant={isFixed ? 'outlined' : 'filled'}
                  color={isFixed ? 'default' : 'primary'}
                />
              );
            })
          }
          value={selectedOptions}
        />
      </Box>
    );
  }

  return (
    <Box
      className={classes.inlineAutocompleteDisplayContainer}
      onClick={handleEdit}
    >
      <Box className={classes.inlineAutocompleteContainer}>
        {sortedAllValues.length > 0 ? (
          sortedAllValues.map((item) =>
            item.isReadOnly ? (
              <Chip
                key={`readonly-${item.value}`}
                label={item.value}
                variant="outlined"
                color="default"
              />
            ) : (
              <Chip key={item.value} label={item.value} variant="filled" />
            )
          )
        ) : (
          <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
            {noOptionsText}
          </Typography>
        )}
      </Box>
      {!disabled && (
        <IconButton
          className={`${classes.inlineAutocompleteEditIcon} edit-icon`}
          onClick={handleEdit}
          size="small"
        >
          <FontAwesomeIcon size="xs" icon={faPen} />
        </IconButton>
      )}
    </Box>
  );
};

InlineEditAutocomplete.displayName = 'InlineEditAutocomplete';

export default InlineEditAutocomplete;
